import React, { useCallback, useState } from 'react';

import analytics from '@repo/analytics';
import WithAuth from '@repo/editor/src/components/WithAuth/WithAuth';
import {
  authenticationStoreSelector,
  useAuthenticationStore,
} from '@repo/editor/src/stores/authenticationStore';
import {
  designsStoreSelector,
  useDesignsStore,
} from '@repo/editor/src/stores/designsStore';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { withEventScope } from '@repo/event-processor';
import { Button } from '@repo/ui/components/Buttons';
import { themeStyle, useTheme } from '@repo/ui/theming';

import { links, getNavigationItemsList } from 'components/HeadMenu/config';
import { HeaderProps } from 'components/HeadMenu/Header/types';
import HeaderUserActions from 'components/HeadMenu/HeaderUserActions';
import {
  LogoContainer,
  MobileWrapper,
  OuterMobileWrapper,
  RightWrapper,
} from 'components/HeadMenu/MobileHeader/styles';
import SideMenu from 'components/HeadMenu/SideMenu/SideMenu';
import {
  MobileNavigationItem,
  MobileTableNavigationItem,
} from 'components/HeadMenu/SideMenu/types';
import { NEW_PROJECT_BUTTON_CLICK } from 'global/analyticsEvents';
import { useEditorAndDashboardUpdateFeature } from 'global/growthbookUtils';
import { buildNewDesignUrl } from 'global/urlUtils';
import { useActiveWorkspaceId } from 'global/utils/workspace/useActiveWorkspaceId';
import {
  mobileRoutingStoreSelector,
  useMobileRoutingStore,
} from 'stores/mobileRoutingStore';

import LogoLink from '../LogoLink';

import Hamburger from './Hamburger';
import MobileHeaderUserActionsOld from './MobileHeaderUserActionsOld';

const disableScrollingInBackground = (): void => {
  document.body.style.overflow = 'hidden';
};

const enableScrollingInBackground = (): void => {
  document.body.style.overflow = 'unset';
};

const MobileHeader: React.FC<HeaderProps> = ({
  inviteReferral,
  onHome,
  hasBanner,
}) => {
  const openAuthentication = useAuthenticationStore(
    authenticationStoreSelector.openAuthentication
  );
  const workspaceId = useActiveWorkspaceId();

  const [showSideMenu, setShowSideMenu] = useState<boolean>(false);

  const { isDarkTheme } = useTheme();

  const navigationItems: MobileNavigationItem[] = [
    ...getNavigationItemsList(isDarkTheme).map(
      (x): MobileTableNavigationItem => ({ ...x, type: 'table' })
    ),
    { type: 'link', label: 'Plans', path: links.PRICING },
  ];

  const handleClose = useCallback(() => {
    setShowSideMenu(false);
    enableScrollingInBackground();
  }, []);

  const handleOpen = useCallback(() => {
    setShowSideMenu(true);
    disableScrollingInBackground();
  }, []);

  const user = useUserStore(userStoreSelector.user);
  const canUserCreateDesign = useDesignsStore(
    designsStoreSelector.canUserCreateDesign
  );

  const tryRouting = useMobileRoutingStore(
    mobileRoutingStoreSelector.tryRouting
  );

  const isNewDesignEnabled = useEditorAndDashboardUpdateFeature();
  const isNewDesignEnabledAndLoggedIn = isNewDesignEnabled && user;

  const onCreateNewDesign = async (): Promise<void> => {
    if (!(await canUserCreateDesign())) {
      return;
    }

    analytics.track(NEW_PROJECT_BUTTON_CLICK);
    tryRouting(() => window.open(buildNewDesignUrl({ workspaceId }), '_blank'));
  };

  return (
    <>
      <SideMenu
        isOpen={showSideMenu}
        navigationItems={navigationItems}
        onClose={handleClose}
      />
      <OuterMobileWrapper hasBanner={!!hasBanner}>
        <MobileWrapper data-testid="header">
          <Hamburger onClick={handleOpen} />
          <LogoContainer>
            <LogoLink
              height="25px"
              inviteReferral={inviteReferral}
              onHome={onHome}
            />
          </LogoContainer>
          <RightWrapper>
            <WithAuth
              renderAuth={(user, onLogOut): React.ReactElement =>
                isNewDesignEnabledAndLoggedIn ? (
                  <HeaderUserActions user={user} onLogOut={onLogOut} />
                ) : (
                  <MobileHeaderUserActionsOld
                    user={user}
                    onCreateNewDesign={onCreateNewDesign}
                    onLogOut={onLogOut}
                  />
                )
              }
              renderUnauth={(): React.ReactElement => (
                <Button
                  height="30px"
                  label="Sign Up –>"
                  name="mobileHeaderSignUpButton"
                  style={{
                    whiteSpace: 'nowrap',
                  }}
                  theme={{
                    fontSize: themeStyle.fontSize12,
                  }}
                  width="85px"
                  onClick={(): void => openAuthentication()}
                />
              )}
            />
          </RightWrapper>
        </MobileWrapper>
      </OuterMobileWrapper>
    </>
  );
};

export default withEventScope(MobileHeader, { feature: 'mobileHeader' });
